import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomLoading from '../../../components/customLoading/CustomLoading';
import Breadcrumbs from '../../../components/BreadCrumbs';
import Pagination from '../../../components/tables/pagination/Pagination';
import { FaBuilding, FaMapMarkerAlt } from 'react-icons/fa';
import { getB2BCampaignFolders, getB2BDistrictFolders, createB2BCampaign } from '../../../services/b2bLeads';
import { normalizePaginatedResponse } from '../../../utils/pagination';
import ShowModal from '../../../utils/ShowModal';
import SearchBar from '../../../components/searchBar/SearchBar';
import Dropdown from '../../../components/inputFields/DropDown';
import { getStates } from '../../../services/csc_api_call';

const B2BFolders = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [campaignData, setCampaignData] = useState({ results: [], count: 0 });
    const [districtData, setDistrictData] = useState({ results: [], count: 0 });
    const [campaignPage, setCampaignPage] = useState(1);
    const [districtPage, setDistrictPage] = useState(1);
    const [activeTab, setActiveTab] = useState('campaign');
    const pageSize = 20;

    const [showAddModal, setShowAddModal] = useState(false);
    const [newCampaignName, setNewCampaignName] = useState('');
    const [addingCampaign, setAddingCampaign] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [states, setStates] = useState([]);
    const [selectedStateId, setSelectedStateId] = useState('');

    useEffect(() => {
        const fetchStatesList = async () => {
            try {
                const res = await getStates();
                if (res?.status === 200) {
                    setStates(res.data.map(s => ({ label: s.name, value: s.id })));
                }
            } catch (err) {
                console.error("Error fetching states:", err);
            }
        };
        fetchStatesList();
    }, []);

    useEffect(() => {
        fetchCampaignFolders();
    }, [campaignPage, searchTerm]);

    useEffect(() => {
        fetchDistrictFolders();
    }, [districtPage, searchTerm, selectedStateId]);

    const fetchCampaignFolders = async () => {
        setLoading(true);
        try {
            const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
            const res = await getB2BCampaignFolders(`page=${campaignPage}&page_size=${pageSize}${searchParam}`);
            if (res?.status === 200) {
                setCampaignData(normalizePaginatedResponse(res.data));
            } else {
                toast.error("Failed to fetch campaign folders");
            }
        } catch (error) {
            toast.error("Error fetching campaign folders");
        } finally {
            setLoading(false);
        }
    };

    const fetchDistrictFolders = async () => {
        setLoading(true);
        try {
            const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
            const stateParam = selectedStateId ? `&state=${selectedStateId}` : '';
            const res = await getB2BDistrictFolders(`page=${districtPage}&page_size=${pageSize}${searchParam}${stateParam}`);
            if (res?.status === 200) {
                setDistrictData(normalizePaginatedResponse(res.data));
            } else {
                toast.error("Failed to fetch district folders");
            }
        } catch (error) {
            toast.error("Error fetching district folders");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        setCampaignPage(1);
        setDistrictPage(1);
    };

    const handleCreateCampaign = async (e) => {
        e.preventDefault();
        if (!newCampaignName.trim()) return;
        setAddingCampaign(true);
        try {
            const res = await createB2BCampaign({ name: newCampaignName.trim() });
            if (res?.status === 200 || res?.status === 201) {
                toast.success("Campaign created successfully");
                setNewCampaignName('');
                setShowAddModal(false);
                fetchCampaignFolders();
            } else {
                toast.error(res?.data?.detail || res?.data?.error || "Failed to create campaign");
            }
        } catch (error) {
            toast.error("Error creating campaign");
        } finally {
            setAddingCampaign(false);
        }
    };

    const handleFolderClick = (type, id) => {
        const query = type === 'campaign' ? `?campaign=${id}` : `?district=${id}`;
        navigate(`/leads-management/b2b/leads${query}`);
    };

    const activeData = activeTab === 'campaign' ? campaignData : districtData;
    const activePage = activeTab === 'campaign' ? campaignPage : districtPage;
    const setActivePage = activeTab === 'campaign' ? setCampaignPage : setDistrictPage;
    const folders = activeData.results || [];

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                <Breadcrumbs items={[{ name: 'Home', link: '/dashboard' }, { name: 'Leads', link: '/leads-management' }, { name: 'B2B' }, { name: 'Folders' }]} />
                <h1 className="text-2xl font-bold text-gray-800 mt-2">B2B Folders</h1>
                <p className="text-gray-600 mt-1">Browse leads by campaign or geographical district</p>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-200 pb-3">
                <div className="flex gap-4">
                    <button
                        type="button"
                        onClick={() => setActiveTab('campaign')}
                        className={`pb-2 px-6 text-sm font-bold transition-all ${activeTab === 'campaign' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        By Campaign
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('district')}
                        className={`pb-2 px-6 text-sm font-bold transition-all ${activeTab === 'district' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        By District
                    </button>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    {activeTab === 'district' && (
                        <div className="w-[180px]">
                            <Dropdown
                                placeholder="Filter by State"
                                options={states}
                                value={selectedStateId}
                                onChange={(val) => {
                                    setSelectedStateId(val);
                                    setDistrictPage(1);
                                }}
                                needSearch
                                compact
                            />
                        </div>
                    )}
                    <div className="w-full sm:w-[220px] md:w-[260px]">
                        <SearchBar
                            placeholder={activeTab === 'campaign' ? "Search campaigns..." : "Search districts..."}
                            onSearch={handleSearch}
                            value={searchTerm}
                            compact
                        />
                    </div>
                    {activeTab === 'campaign' && (
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="px-4 py-1.5 bg-primary text-white text-xs font-black rounded-lg shadow-md hover:bg-primary/95 transition-all active:scale-95 whitespace-nowrap"
                        >
                            + Add Campaign
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <CustomLoading size={80} />
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {activeTab === 'campaign' ? (
                            folders.map((folder) => (
                                <div
                                    key={folder.campaign_id ?? folder.id}
                                    onClick={() => handleFolderClick('campaign', folder.campaign_id)}
                                    className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-md hover:border-primary transition-all cursor-pointer group"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="bg-blue-50 p-3 rounded-lg text-blue-600 group-hover:bg-primary group-hover:text-white transition-colors">
                                            <FaBuilding size={24} />
                                        </div>
                                        <div className="text-xs font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded capitalize">
                                            Campaign
                                        </div>
                                    </div>
                                    <h3 className="font-bold text-gray-800 text-lg mb-1 truncate">{folder.campaign_name || 'Unnamed Campaign'}</h3>
                                    <p className="text-primary font-bold text-sm flex items-center gap-1">
                                        {folder.lead_count} <span className="text-gray-500 font-medium text-xs">Leads</span>
                                    </p>
                                </div>
                            ))
                        ) : (
                            folders.map((folder) => (
                                <div
                                    key={folder.district_id ?? folder.id}
                                    onClick={() => handleFolderClick('district', folder.district_id)}
                                    className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-md hover:border-primary transition-all cursor-pointer group"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="bg-emerald-50 p-3 rounded-lg text-emerald-600 group-hover:bg-primary group-hover:text-white transition-colors">
                                            <FaMapMarkerAlt size={24} />
                                        </div>
                                        <div className="text-xs font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded capitalize">
                                            District
                                        </div>
                                    </div>
                                    <h3 className="font-bold text-gray-800 text-lg mb-1 truncate">{folder.district_name || 'Unknown District'}</h3>
                                    <p className="text-primary font-bold text-sm flex items-center gap-1">
                                        {folder.lead_count} <span className="text-gray-500 font-medium text-xs">Leads</span>
                                    </p>
                                </div>
                            ))
                        )}
                    </div>

                    {folders.length === 0 && (
                        <div className="text-center py-10 text-gray-500">
                            No folders found.
                        </div>
                    )}

                    {activeData.count > 0 && (
                        <Pagination
                            currentPage={activePage}
                            pageSize={pageSize}
                            totalEntries={activeData.count}
                            onPageChange={setActivePage}
                            data={activeData}
                        />
                    )}
                </>
            )}
            {showAddModal && (
                <ShowModal
                    isOpen={showAddModal}
                    onRequestClose={() => setShowAddModal(false)}
                    title="Add Campaign"
                    modalWidth="450px"
                    componentToPass={
                        <form onSubmit={handleCreateCampaign} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1">Campaign Name</label>
                                <input
                                    type="text"
                                    required
                                    value={newCampaignName}
                                    onChange={(e) => setNewCampaignName(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                    placeholder="Enter campaign name..."
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="px-4 py-2 text-xs font-bold text-gray-600 border border-gray-200 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={addingCampaign}
                                    className="px-5 py-2 text-xs font-black bg-primary text-white rounded-lg disabled:bg-gray-300 active:scale-95 transition-all"
                                >
                                    {addingCampaign ? 'Saving...' : 'Save Campaign'}
                                </button>
                            </div>
                        </form>
                    }
                />
            )}
        </div>
    );
};

export default B2BFolders;
